import { buildMapData, createDemoBuilds, createId, normalizeBuildRow, normalizeHandle, normalizeText } from './maps.js'

const PROFILE_KEY = 'build-destroy-profile'
const ACCESS_PROFILE_COOKIE = 'build-destroy-access-profile'
const LOCAL_BUILDS_KEY = 'build-destroy-local-builds'
const CLIENT_TOKEN_KEY = 'build-destroy-client-token'
const ACCESS_PROFILE_COOKIE_MAX_AGE = 60 * 60 * 24 * 365

const apiBaseUrl = String(import.meta.env.VITE_API_BASE_URL || '').replace(/\/+$/, '')
const profileImageEndpoint = import.meta.env.VITE_PROFILE_IMAGE_URL || `${apiBaseUrl}/api/profile-image`
const audioBaseUrl = String(import.meta.env.VITE_AUDIO_BASE_URL || '/audio').replace(/\/+$/, '')
const avatarCache = new Map()
const LOCAL_DEMO_BUILDER_LEADERS = [
    { id: 'demo-builder-ddd', name: 'ddd', xHandle: '', avatarUrl: '', score: 21, buildsPublished: 1, attemptsReceived: 2, holds: 1, breaches: 1, avgComplexity: 18 },
    { id: 'demo-builder-leo', name: 'Leo', xHandle: '', avatarUrl: '', score: 16, buildsPublished: 1, attemptsReceived: 6, holds: 0, breaches: 6, avgComplexity: 24 },
    { id: 'demo-builder-builder', name: 'Builder', xHandle: '', avatarUrl: '', score: 13, buildsPublished: 1, attemptsReceived: 1, holds: 1, breaches: 0, avgComplexity: 10 },
    { id: 'demo-builder-dome', name: 'Dome', xHandle: 'domenicodd', avatarUrl: '', score: 12, buildsPublished: 1, attemptsReceived: 5, holds: 0, breaches: 5, avgComplexity: 26 }
]
const LOCAL_DEMO_DESTROYER_LEADERS = [
    { id: 'demo-destroyer-leo', name: 'Leo', xHandle: '', avatarUrl: '', score: 84, attempts: 8, wins: 8, bestBombs: 2, avgBombs: 2.4, fastestMs: null },
    { id: 'demo-destroyer-dome', name: 'Dome', xHandle: '', avatarUrl: '', score: 31, attempts: 2, wins: 2, bestBombs: 3, avgBombs: 3, fastestMs: null },
    { id: 'demo-destroyer-dome-x', name: 'Dome', xHandle: 'domenicodd', avatarUrl: '', score: 30, attempts: 2, wins: 2, bestBombs: 3, avgBombs: 3, fastestMs: null },
    { id: 'demo-destroyer-ddd', name: 'ddd', xHandle: '', avatarUrl: '', score: 14, attempts: 1, wins: 1, bestBombs: 2, avgBombs: 2, fastestMs: null }
]

export function isBackendConfigured() {
    return import.meta.env.VITE_USE_LOCAL_DEMO !== 'true'
}

export function getAudioAssetUrl(fileName) {
    const cleanFileName = String(fileName || '').replace(/^\/+/, '')
    return `${audioBaseUrl}/${cleanFileName}`
}

export function getAccessProfile() {
    const raw = getCookie(ACCESS_PROFILE_COOKIE)
    if (!raw) {
        return null
    }

    try {
        const profile = normalizeAccessProfile(JSON.parse(decodeURIComponent(raw)))
        return profile.displayName ? profile : null
    } catch (error) {
        console.warn('Unable to read access profile cookie', error)
        return null
    }
}

export function saveAccessProfile(profile) {
    const nextProfile = normalizeAccessProfile(profile)

    if (!nextProfile.displayName) {
        return null
    }

    const secureFlag = window.location.protocol === 'https:' ? '; Secure' : ''
    document.cookie = `${ACCESS_PROFILE_COOKIE}=${encodeURIComponent(JSON.stringify(nextProfile))}; Max-Age=${ACCESS_PROFILE_COOKIE_MAX_AGE}; Path=/; SameSite=Lax${secureFlag}`
    return nextProfile
}

export function getProfile() {
    const saved = readJson(PROFILE_KEY)

    if (saved?.builderId && saved?.displayName) {
        return saved
    }

    return {
        builderId: createId('builder'),
        displayName: '',
        xHandle: '',
        avatarUrl: ''
    }
}

function normalizeAccessProfile(profile) {
    return {
        displayName: normalizeText(profile?.displayName, '', 40),
        xHandle: normalizeHandle(profile?.xHandle) || ''
    }
}

export function saveProfile(profile) {
    const nextProfile = {
        builderId: profile.builderId || createId('builder'),
        displayName: normalizeText(profile.displayName, 'Builder', 40),
        xHandle: normalizeHandle(profile.xHandle) || '',
        avatarUrl: normalizeAvatarUrl(profile.avatarUrl)
    }

    localStorage.setItem(PROFILE_KEY, JSON.stringify(nextProfile))
    return nextProfile
}

export async function ensureBuilder(profile) {
    const savedProfile = saveProfile(profile)
    const avatarUrl = await resolveProfileAvatar(savedProfile.xHandle) || savedProfile.avatarUrl || ''
    const builder = {
        ...savedProfile,
        avatarUrl
    }

    if (avatarUrl && avatarUrl !== savedProfile.avatarUrl) {
        saveProfile(builder)
    }

    return builder
}

export async function publishBuild({ profile, title, blocks, flag, ogImageBlob = null, previewImageBlob = null }) {
    const builder = await ensureBuilder(profile)
    const buildId = createId()
    const previewImageUrl = !isBackendConfigured() && previewImageBlob ? await blobToDataUrl(previewImageBlob) : ''
    const mapData = buildMapData({
        title,
        builderName: builder.displayName,
        xHandle: builder.xHandle,
        builderId: builder.builderId,
        blocks,
        flag,
        previewImageUrl
    })

    if (!isBackendConfigured()) {
        const localBuild = normalizeBuildRow({
            id: buildId,
            title: mapData.title,
            map_data: mapData,
            block_count: mapData.blockCount,
            budget_used: mapData.budgetUsed,
            complexity: mapData.complexity,
            bombs_granted: mapData.bombsGranted,
            created_at: mapData.createdAt,
            source: 'local'
        })

        saveLocalBuild(localBuild)
        return localBuild
    }

    const data = await apiRequest('/api/publish-build', {
        method: 'POST',
        body: {
            clientToken: getClientToken(),
            profile: builder,
            title: mapData.title,
            blocks: mapData.blocks,
            flag: mapData.flag,
            ogImage: await blobToBase64Payload(ogImageBlob),
            previewImage: await blobToBase64Payload(previewImageBlob)
        }
    })

    return normalizeBuildRow(data.build)
}

export async function fetchBuildById(id) {
    const localBuild = getLocalBuilds().find((build) => build.id === id)
    if (localBuild) {
        return localBuild
    }

    if (!isBackendConfigured()) {
        return null
    }

    try {
        const data = await apiRequest(`/api/builds/${encodeURIComponent(id)}`)
        return normalizeBuildRow(data.build)
    } catch (error) {
        if (error.status === 404) {
            return null
        }
        throw error
    }
}

export async function fetchAttemptById(id) {
    if (!isBackendConfigured()) {
        return null
    }

    try {
        const data = await apiRequest(`/api/attempts/${encodeURIComponent(id)}`)
        return normalizeAttemptRow(data.attempt)
    } catch (error) {
        if (error.status === 404) {
            return null
        }
        throw error
    }
}

export async function fetchBuilds({ complexity = 'all', cursor = null, limit = 18 } = {}) {
    if (!isBackendConfigured()) {
        return filterLocalBuilds(complexity).slice(0, limit)
    }

    const params = new URLSearchParams({
        complexity,
        limit: String(limit)
    })
    if (cursor?.createdAt) {
        params.set('cursor', cursor.createdAt)
    }

    const data = await apiRequest(`/api/builds?${params.toString()}`)
    return data.builds.map(normalizeBuildRow)
}

export async function fetchLeaderboards({ limit = 12 } = {}) {
    if (!isBackendConfigured()) {
        return createLocalLeaderboards(limit)
    }

    const params = new URLSearchParams({ limit: String(limit) })
    const data = await apiRequest(`/api/leaderboards?${params.toString()}`)

    return {
        builders: data.builders.map(normalizeBuilderLeaderboardRow),
        destroyers: data.destroyers.map(normalizeDestroyerLeaderboardRow)
    }
}

export async function recordAttempt({ build, destroyerName, destroyerXHandle = '', won, bombsUsed, durationMs, ogImageBlob = null }) {
    const normalizedDestroyerName = normalizeText(destroyerName, 'Destroyer', 40)
    const normalizedDestroyerXHandle = normalizeHandle(destroyerXHandle) || ''
    const avatarUrl = await resolveProfileAvatar(normalizedDestroyerXHandle)

    if (!isRemoteBuild(build) || !isBackendConfigured()) {
        updateLocalStats(build.id, won)
        return {
            id: null,
            buildId: build.id,
            destroyerName: normalizedDestroyerName,
            xHandle: normalizedDestroyerXHandle,
            won,
            bombsUsed,
            durationMs,
            avatarUrl,
            ogImageUrl: '',
            createdAt: new Date().toISOString()
        }
    }

    const data = await apiRequest('/api/record-attempt', {
        method: 'POST',
        body: {
            clientToken: getClientToken(),
            buildId: build.id,
            destroyerName: normalizedDestroyerName,
            destroyerXHandle: normalizedDestroyerXHandle,
            won,
            bombsUsed,
            durationMs,
            avatarUrl,
            ogImage: won ? await blobToBase64Payload(ogImageBlob) : null
        }
    })

    return normalizeAttemptRow(data.attempt)
}

export async function resolveProfileAvatar(xHandle) {
    const handle = normalizeHandle(xHandle)

    if (!handle || !profileImageEndpoint) {
        return ''
    }

    const cacheKey = handle.toLowerCase()
    if (avatarCache.has(cacheKey)) {
        return avatarCache.get(cacheKey)
    }

    try {
        const url = new URL(profileImageEndpoint, window.location.origin)
        url.searchParams.set('handle', handle)
        const response = await fetch(url.toString())

        if (!response.ok) {
            avatarCache.set(cacheKey, '')
            return ''
        }

        const payload = await response.json()
        const avatarUrl = normalizeAvatarUrl(payload.avatarUrl)
        avatarCache.set(cacheKey, avatarUrl)
        return avatarUrl
    } catch (error) {
        console.warn('Unable to resolve X profile image', error)
        avatarCache.set(cacheKey, '')
        return ''
    }
}

function normalizeAttemptRow(row) {
    if (!row) {
        return null
    }

    return {
        id: row.id,
        buildId: row.build_id || row.buildId,
        destroyerName: row.destroyer_name || row.destroyerName || 'Destroyer',
        xHandle: row.destroyer_x_handle || row.xHandle || '',
        won: Boolean(row.won),
        bombsUsed: row.bombs_used ?? row.bombsUsed ?? 0,
        durationMs: row.duration_ms ?? row.durationMs ?? null,
        avatarUrl: row.avatar_url || row.avatarUrl || '',
        ogImageUrl: row.og_image_url || row.ogImageUrl || '',
        createdAt: row.created_at || row.createdAt || new Date().toISOString()
    }
}

function normalizeBuilderLeaderboardRow(row) {
    return {
        id: row.builder_id || row.id,
        name: row.display_name || row.name || 'Builder',
        xHandle: row.x_handle || row.xHandle || '',
        avatarUrl: row.avatar_url || row.avatarUrl || '',
        score: Number(row.rank_score || row.score || 0),
        buildsPublished: Number(row.builds_published || row.buildsPublished || 0),
        attemptsReceived: Number(row.attempts_received || row.attemptsReceived || 0),
        holds: Number(row.holds || 0),
        breaches: Number(row.breaches || 0),
        avgComplexity: Number(row.avg_complexity || row.avgComplexity || 0)
    }
}

function normalizeDestroyerLeaderboardRow(row) {
    return {
        id: row.player_key || row.id,
        name: row.display_name || row.destroyer_name || row.name || 'Destroyer',
        xHandle: row.x_handle || row.xHandle || '',
        avatarUrl: row.avatar_url || row.avatarUrl || '',
        score: Number(row.rank_score || row.score || 0),
        attempts: Number(row.attempts || 0),
        wins: Number(row.wins || 0),
        bestBombs: row.best_bombs ?? row.bestBombs ?? null,
        avgBombs: row.avg_bombs ?? row.avgBombs ?? null,
        fastestMs: row.fastest_ms ?? row.fastestMs ?? null
    }
}

function createLocalLeaderboards(limit) {
    const builders = new Map()

    for (const build of [...getLocalBuilds(), ...createDemoBuilds()]) {
        const id = build.builderId || build.builderName
        const existing = builders.get(id) || {
            id,
            name: build.builderName,
            xHandle: build.xHandle,
            avatarUrl: '',
            score: 0,
            buildsPublished: 0,
            attemptsReceived: 0,
            holds: 0,
            breaches: 0,
            avgComplexity: 0,
            totalComplexity: 0
        }
        const holds = Math.max(0, build.playCount - build.winCount)

        existing.buildsPublished += 1
        existing.attemptsReceived += build.playCount
        existing.holds += holds
        existing.breaches += build.winCount
        existing.totalComplexity += build.complexity
        existing.score += holds * 5 + build.playCount + build.complexity * 0.1
        builders.set(id, existing)
    }

    const builderRows = [...builders.values()]
        .map((builder) => ({
            ...builder,
            avgComplexity: builder.buildsPublished
                ? roundTo(builder.totalComplexity / builder.buildsPublished, 1)
                : 0
        }))

    return {
        builders: mergeDemoLeaderboardRows(builderRows, LOCAL_DEMO_BUILDER_LEADERS)
            .sort((a, b) => b.score - a.score || b.holds - a.holds)
            .slice(0, limit),
        destroyers: LOCAL_DEMO_DESTROYER_LEADERS.slice(0, limit)
    }
}

function mergeDemoLeaderboardRows(rows, demoRows) {
    const usedKeys = new Set(rows.map((row) => String(row.xHandle || row.name || row.id).toLowerCase()))
    const mergedRows = [...rows]

    for (const demoRow of demoRows) {
        const key = String(demoRow.xHandle || demoRow.name || demoRow.id).toLowerCase()
        if (usedKeys.has(key)) {
            continue
        }
        mergedRows.push(demoRow)
        usedKeys.add(key)
    }

    return mergedRows
}

function normalizeAvatarUrl(value) {
    if (!value) {
        return ''
    }

    try {
        const url = new URL(String(value))
        return url.protocol === 'https:' || url.protocol === 'http:' ? url.toString() : ''
    } catch {
        return ''
    }
}

function roundTo(value, decimals) {
    const scale = 10 ** decimals
    return Math.round(value * scale) / scale
}

function getClientToken() {
    const savedToken = localStorage.getItem(CLIENT_TOKEN_KEY)
    if (savedToken) {
        return savedToken
    }

    const token = createId()
    localStorage.setItem(CLIENT_TOKEN_KEY, token)
    return token
}

function getCookie(name) {
    const prefix = `${name}=`
    return document.cookie
        .split(';')
        .map((cookie) => cookie.trim())
        .find((cookie) => cookie.startsWith(prefix))
        ?.slice(prefix.length) || ''
}

async function blobToBase64Payload(blob) {
    if (!blob) {
        return null
    }

    const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.addEventListener('load', () => resolve(String(reader.result || '')))
        reader.addEventListener('error', () => reject(reader.error || new Error('Unable to read image.')))
        reader.readAsDataURL(blob)
    })
    const separatorIndex = dataUrl.indexOf(',')
    const meta = dataUrl.slice(0, separatorIndex)

    return {
        contentType: meta.match(/^data:([^;]+);base64$/)?.[1] || blob.type || 'image/png',
        data: dataUrl.slice(separatorIndex + 1)
    }
}

async function apiRequest(path, { method = 'GET', body = null } = {}) {
    const response = await fetch(`${apiBaseUrl}${path}`, {
        method,
        headers: body ? { 'Content-Type': 'application/json' } : undefined,
        body: body ? JSON.stringify(body) : undefined
    })
    const payload = await response.json().catch(() => ({}))

    if (!response.ok) {
        const error = new Error(payload?.error || `Request failed with status ${response.status}.`)
        error.status = response.status
        throw error
    }

    return payload
}

function isRemoteBuild(build) {
    return build?.source === 'neon'
        || build?.source === 'remote'
}

function blobToDataUrl(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.addEventListener('load', () => resolve(String(reader.result || '')))
        reader.addEventListener('error', () => reject(reader.error || new Error('Unable to read image.')))
        reader.readAsDataURL(blob)
    })
}

function readJson(key, fallback = null) {
    try {
        const raw = localStorage.getItem(key)
        return raw ? JSON.parse(raw) : fallback
    } catch (error) {
        console.warn(`Unable to read ${key}`, error)
        return fallback
    }
}

function getLocalBuilds() {
    return readJson(LOCAL_BUILDS_KEY, []).map(normalizeBuildRow)
}

function saveLocalBuild(build) {
    const builds = getLocalBuilds().filter((savedBuild) => savedBuild.id !== build.id)
    builds.unshift({ ...build, source: 'local' })
    localStorage.setItem(LOCAL_BUILDS_KEY, JSON.stringify(builds.slice(0, 40)))
}

function filterLocalBuilds(complexity) {
    const builds = [...getLocalBuilds(), ...createDemoBuilds()]

    if (complexity === 'low') {
        return builds.filter((build) => build.complexity <= 12)
    }

    if (complexity === 'medium') {
        return builds.filter((build) => build.complexity >= 13 && build.complexity <= 24)
    }

    if (complexity === 'high') {
        return builds.filter((build) => build.complexity >= 25)
    }

    return builds
}

function updateLocalStats(buildId, won) {
    const builds = getLocalBuilds()
    const build = builds.find((savedBuild) => savedBuild.id === buildId)

    if (!build) {
        return
    }

    build.playCount += 1
    build.winCount += won ? 1 : 0
    localStorage.setItem(LOCAL_BUILDS_KEY, JSON.stringify(builds))
}
